import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import { faPlus } from '@fortawesome/pro-solid-svg-icons'
import { TwoColumn } from 'components/layout'
import { Button } from 'components/buttons'
import { Spinner } from 'components/loaders'
import { SimpleTable } from 'components/tables'
import { TableButton } from '@features/admin/features/approval-domain-approvers'
import { useProjectTasks } from '../../../hooks/useProjectTasks'
import { useManageProjectTasks } from '../../../hooks/useManageProjectTasks'

interface Props {
  project: AdminProject
}

const pageSize = 10

const columnHelper = createColumnHelper<AdminProjectTask>()

export const TasksSection: FC<Props> = ({ project }) => {
  const { t } = useTranslation()
  const { data: tasks = [], isLoading } = useProjectTasks(project.id)
  const { addTask, renameTask, removeTask } = useManageProjectTasks(project)

  const columns = [
    columnHelper.accessor('name', {
      id: 'name',
      header: t('features.projectsAdmin.projectDetails.taskName'),
    }),
    columnHelper.display({
      id: 'action',
      header: t('common.action'),
      meta: { className: 'w-px whitespace-nowrap' },
      cell: ({ row }) => (
        <div className="flex gap-2 items-center">
          <TableButton
            variant="primary"
            onClick={() => renameTask(row.original)}
          >
            {t('common.edit')}
          </TableButton>
          |
          <TableButton
            variant="danger"
            onClick={() => removeTask(row.original)}
          >
            {t('common.delete')}
          </TableButton>
        </div>
      ),
    }),
  ] as ColumnDef<AdminProjectTask>[]

  const renderTasks = () => {
    if (isLoading) return <Spinner />

    if (tasks.length === 0)
      return (
        <div className="flex flex-col items-center text-sm gap-4 py-8 text-neutral-700 rounded text-center border border-neutral-200">
          {t('features.projectsAdmin.projectDetails.noTasks')}
          <Button
            icon={faPlus}
            variant="outlined"
            className="text-sm"
            onClick={addTask}
          >
            {t('features.projectsAdmin.projectDetails.addTask')}
          </Button>
        </div>
      )

    return (
      <SimpleTable
        columns={columns}
        data={tasks}
        defaultSortColumn="name"
        pageSize={pageSize}
      />
    )
  }

  return (
    <TwoColumn
      label={t('features.projectsAdmin.projectDetails.tasks')}
      description={t('features.projectsAdmin.projectDetails.tasksDescription')}
      leftSlot={
        <Button
          onClick={addTask}
          icon={faPlus}
          variant="outlined"
          className="text-sm"
        >
          {t('features.projectsAdmin.projectDetails.addTask')}
        </Button>
      }
    >
      <div className="flex flex-col w-[825px]" data-testid="project-tasks">
        {renderTasks()}
      </div>
    </TwoColumn>
  )
}
